import { useCallback, useEffect, useMemo } from 'react'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiSelectBox from '../../../../../../features/components/Form/MultiSelectBox'

import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { getValidFormattedExchangeTargetingOptions } from '../../../../lineItemHelpers'

import {
  dv360BulkTargetingWasUpdatedSelector,
  dv360ExchangeTargetingOptionsSelector,
  dv360TargetingSelector,
  getDv360ExchangeTargetingOptionsIsLoadingSelector,
  updateDv360BulkTargetingErrorSelector,
  updateDv360BulkTargetingIsLoadingSelector
} from '../../../../../../modules/selectors/dv360Constants'
import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import {
  clearGetDv360ExchangeTargetingOptions,
  clearUpdateDv360BulkTargeting,
  getDv360ExchangeTargetingOptions,
  updateDv360BulkTargeting
} from '../../../../../../modules/actions/dv360Constants'

import { CUSTOM_EXCHANGES } from '../../../../UploadDV360AdCreative/UploadDV360AdCreativeForm/LineItemForm/fields'
import { HISTORY_META_EDIT_SECTION, TYPE_LINE_ITEM } from '../../../../../../constants/changeHistory'
import { targetingTypes } from '../../../LineItemDv360Create/fields'
import { getInitialValues } from './fields'

function ExchangesSectionForm({ editItemData: item, title, ...formProps }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const targeting = useSelector(dv360TargetingSelector)
  const dv360BulkTargetingWasUpdated = useSelector(dv360BulkTargetingWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)
  const getDv360ExchangeTargetingOptionsIsLoading = useSelector(getDv360ExchangeTargetingOptionsIsLoadingSelector)
  const dv360ExchangeTargetingOptions = useSelector(dv360ExchangeTargetingOptionsSelector)

  const initialValues = useMemo(() => {
    return getInitialValues(targeting)
  }, [targeting])

  const handleSubmit = useCallback(
    values => {
      const requests = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_EXCHANGE,
          assigned_targeting_options: values[CUSTOM_EXCHANGES].map(exchange => ({
            exchange: exchange.value
          }))
        }
      ]

      const updateData = {
        account: selectedAdAccountId,
        line_item_id: item.id,
        requests,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title,
          override: {
            item_id: item.id,
            item_type: TYPE_LINE_ITEM,
            action: 'update'
          }
        }
      }

      dispatch(updateDv360BulkTargeting(updateData, true))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values, setFieldValue } = formik

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateDv360BulkTargeting())
  }, [dispatch])

  const formattedExchangeTargetingOptions = useMemo(() => {
    return getValidFormattedExchangeTargetingOptions(dv360ExchangeTargetingOptions)
  }, [dv360ExchangeTargetingOptions])

  useEffect(() => {
    dispatch(
      getDv360ExchangeTargetingOptions({
        account: selectedAdAccountId
      })
    )
  }, [dispatch, selectedAdAccountId])

  useEffect(() => {
    return () => {
      dispatch(clearGetDv360ExchangeTargetingOptions())
    }
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || dv360BulkTargetingWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={updateDv360BulkTargetingErrorSelector}
      isLoadingSelector={updateDv360BulkTargetingIsLoadingSelector}
      {...formProps}
    >
      <MultiSelectBox
        placeholder={t('Custom exchanges')}
        options={formattedExchangeTargetingOptions}
        name={CUSTOM_EXCHANGES}
        value={values[CUSTOM_EXCHANGES]}
        isLoading={getDv360ExchangeTargetingOptionsIsLoading}
        setFieldValue={setFieldValue}
      />
    </EditForm>
  )
}

export default ExchangesSectionForm
